import React, { useEffect, useState } from 'react';
import fnc from '../mymod/commFunction';
import './OmrSubmissionResultsModal.scss';
import KatexText from './KatexText';

const questionTypeLabel = {
  objective: '객관식',
  shortAnswer: '단답형',
  subjective: '서술형'
};

const MIN_IMAGE_ZOOM = 0.5;
const MAX_IMAGE_ZOOM = 3;
const IMAGE_ZOOM_STEP = 0.25;

const formatAnswerForKatex = (value, questionType) => {
  const source = String(value ?? '').trim();
  if (!source || source.includes('$') || /\\[([]/.test(source) || questionType === 'subjective') return source;
  const looksLikeMath = /[0-9\\^_{}=+\-*/<>≤≥°π√]/.test(source);
  if (!looksLikeMath) return source;
  return `$${source.replace(/°/g, '^{\\circ}')}$`;
};

const OmrSubmissionResultsModal = ({ parentCode, questions, onClose }) => {
  const [submissions, setSubmissions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [details, setDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [deletingStCode, setDeletingStCode] = useState('');
  const [studentImage, setStudentImage] = useState(null);

  useEffect(() => {
    loadSubmissions();
  }, [parentCode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (studentImage) {
        setStudentImage(null);
      } else {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, studentImage]);

  const loadSubmissions = () => {
    const acaCode = fnc.getCookie('acaCode');
    setIsLoading(true);
    fnc.sendAxios('/select/lmsTec/course/omrSubmissions', { acaCode, parentCode }, (res) => {
      setIsLoading(false);
      if (Array.isArray(res)) {
        setSubmissions(res);
      } else {
        setSubmissions([]);
      }
    });
  };

  const selectStudent = (student) => {
    const acaCode = fnc.getCookie('acaCode');
    setSelectedStudent(student);
    setDetails([]);
    setIsDetailLoading(true);
    fnc.sendAxios(
      '/select/lmsTec/course/omrSubmissionDetail',
      { acaCode, parentCode, stCode: student.stCode },
      (res) => {
        setIsDetailLoading(false);
        setDetails(Array.isArray(res) ? res : []);
      }
    );
  };

  const openStudentImage = (student) => {
    if (!student.answerSheetImageKey) return;
    setStudentImage({
      studentName: student.stName || student.stCode,
      imageName: student.answerSheetImageName || '학생 원본 답안지',
      url: '',
      status: 'loading',
      error: '',
      zoom: 1
    });
    fnc.sendAxios(
      '/get-image-path',
      { imagePath: student.answerSheetImageKey, sec: 600 },
      (res) => {
        if (res?.url) {
          setStudentImage((current) => current ? { ...current, url: res.url, status: 'success' } : null);
        } else {
          setStudentImage((current) => current ? {
            ...current,
            status: 'error',
            error: '학생 원본 이미지를 불러오지 못했습니다.'
          } : null);
        }
      },
      () => {
        setStudentImage((current) => current ? {
          ...current,
          status: 'error',
          error: '학생 원본 이미지를 불러오지 못했습니다.'
        } : null);
      }
    );
  };

  const setImageZoom = (nextZoom) => {
    setStudentImage((current) => current ? {
      ...current,
      zoom: Math.min(MAX_IMAGE_ZOOM, Math.max(MIN_IMAGE_ZOOM, nextZoom))
    } : null);
  };

  const changeImageZoom = (amount) => {
    setStudentImage((current) => current ? {
      ...current,
      zoom: Math.min(MAX_IMAGE_ZOOM, Math.max(MIN_IMAGE_ZOOM, (current.zoom || 1) + amount))
    } : null);
  };

  const deleteSubmission = (student) => {
    const confirmed = window.confirm(
      `${student.stName || student.stCode} 학생의 제출 결과를 삭제하시겠습니까?\n삭제하면 이 시험에 다시 응시할 수 있습니다.`
    );
    if (!confirmed) return;

    const acaCode = fnc.getCookie('acaCode');
    setDeletingStCode(student.stCode);
    fnc.sendAxios(
      '/delete/lmsTec/course/omrSubmission',
      { acaCode, parentCode, stCode: student.stCode },
      (res) => {
        setDeletingStCode('');
        if (res?.success) {
          setSubmissions((current) => current.filter((item) => item.stCode !== student.stCode));
          if (selectedStudent?.stCode === student.stCode) {
            setSelectedStudent(null);
            setDetails([]);
          }
          alert('제출 결과를 삭제했습니다. 학생이 다시 시험을 볼 수 있습니다.');
        } else {
          alert('제출 결과를 삭제하지 못했습니다.');
        }
      }
    );
  };

  const getQuestion = (munNo) => questions.find((item, index) => Number(item.munNo || index + 1) === Number(munNo)) || {};

  return (
    <div className="omr-results-modal-backdrop" role="presentation">
      <section className="omr-results-modal" role="dialog" aria-modal="true" aria-labelledby="omr-results-title">
        <header>
          <div>
            <h2 id="omr-results-title">제출 학생 결과</h2>
            <p>학생별 마킹 결과를 확인하거나 제출을 삭제하여 재시험을 허용할 수 있습니다.</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">×</button>
        </header>

        <div className="submission-summary">
          <strong>제출 {submissions.length}명</strong>
          <button type="button" onClick={loadSubmissions} disabled={isLoading}>새로고침</button>
        </div>

        <div className="submission-table-wrap">
          <table className="submission-table">
            <thead>
              <tr>
                <th>No</th>
                <th>학생</th>
                <th>점수</th>
                <th>제출일시</th>
                <th>마킹</th>
                <th>원본</th>
                <th>재시험</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="7" className="empty-row">제출 결과를 불러오는 중입니다.</td></tr>
              ) : submissions.length === 0 ? (
                <tr><td colSpan="7" className="empty-row">아직 제출한 학생이 없습니다.</td></tr>
              ) : submissions.map((student, index) => (
                <tr key={student.stCode} className={selectedStudent?.stCode === student.stCode ? 'selected' : ''}>
                  <td>{index + 1}</td>
                  <td>{student.stName || student.stCode}</td>
                  <td>{student.score === '' ? '-' : `${Number(student.score).toFixed(1)}점`}</td>
                  <td>{student.markingDate || '-'}</td>
                  <td>
                    <button type="button" className="detail-button" onClick={() => selectStudent(student)}>결과 보기</button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="image-button"
                      onClick={() => openStudentImage(student)}
                      disabled={!student.answerSheetImageKey}
                      title={student.answerSheetImageKey ? '학생이 제출한 원본 답안지 보기' : '저장된 원본 이미지가 없습니다.'}
                    >
                      {student.answerSheetImageKey ? '원본 이미지' : '원본 없음'}
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="retake-button"
                      onClick={() => deleteSubmission(student)}
                      disabled={Boolean(deletingStCode)}
                    >
                      {deletingStCode === student.stCode ? '삭제 중…' : '삭제·재시험'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedStudent && (
          <div className="marking-detail">
            <div className="marking-detail-title">
              <strong>{selectedStudent.stName || selectedStudent.stCode} 학생 마킹 결과</strong>
              <span>{selectedStudent.score === '' ? '' : `총점 ${Number(selectedStudent.score).toFixed(1)}점`}</span>
            </div>
            <div className="submission-table-wrap detail-wrap">
              <table className="submission-table detail-table">
                <thead>
                  <tr>
                    <th>문항</th>
                    <th>유형</th>
                    <th>정답·채점 기준</th>
                    <th>학생 마킹</th>
                    <th>결과</th>
                    <th>배점</th>
                  </tr>
                </thead>
                <tbody>
                  {isDetailLoading ? (
                    <tr><td colSpan="6" className="empty-row">마킹 결과를 불러오는 중입니다.</td></tr>
                  ) : details.length === 0 ? (
                    <tr><td colSpan="6" className="empty-row">저장된 문항별 마킹 결과가 없습니다.</td></tr>
                  ) : details.map((detail) => {
                    const question = getQuestion(detail.munNo);
                    const questionType = question.questionType || 'objective';
                    return (
                      <tr key={detail.munNo}>
                        <td>{detail.munNo}</td>
                        <td>{questionTypeLabel[questionType]}</td>
                        <td className="answer-cell">
                          {questionType === 'subjective' ? (
                            <>
                              {question.modelAnswerText && (
                                <div className="answer-line model-answer-line">
                                  <strong>모범 답안</strong>
                                  <KatexText value={question.modelAnswerText} />
                                </div>
                              )}
                              {(question.criteria || []).map((criterion, index) => (
                                <div className="answer-line criterion-line" key={index}>
                                  <KatexText
                                    value={`기준 ${index + 1}: ${criterion.text} (${criterion.valueType === 'ratio' ? `배율 ${criterion.value}` : `${criterion.value}점`})`}
                                  />
                                </div>
                              ))}
                              {!question.modelAnswerText && (question.criteria || []).length === 0 && '서술형 채점 정보 없음'}
                            </>
                          ) : (
                            <KatexText value={formatAnswerForKatex(detail.dap || '-', questionType)} />
                          )}
                        </td>
                        <td className="marking-cell">
                          <KatexText value={formatAnswerForKatex(detail.markingValue || '-', questionType)} renderBareLatex />
                        </td>
                        <td className={detail.OX === 'O' ? 'correct' : detail.OX === 'X' ? 'incorrect' : ''}>{detail.OX || '-'}</td>
                        <td>{detail.point === '' ? '-' : `${detail.point}점`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <footer>
          <button type="button" onClick={onClose}>닫기</button>
        </footer>
      </section>

      {studentImage && (
        <div
          className="student-answer-image-backdrop"
          role="presentation"
          onMouseDown={() => setStudentImage(null)}
        >
          <section
            className="student-answer-image-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="student-answer-image-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <h3 id="student-answer-image-title">{studentImage.studentName} 학생 원본 답안지</h3>
                <span>{studentImage.imageName}</span>
              </div>
              <button type="button" aria-label="원본 이미지 닫기" onClick={() => setStudentImage(null)}>×</button>
            </header>
            {studentImage.url && (
              <div className="student-answer-image-modal__toolbar" aria-label="원본 이미지 확대 및 축소">
                <button
                  type="button"
                  onClick={() => changeImageZoom(-IMAGE_ZOOM_STEP)}
                  disabled={studentImage.zoom <= MIN_IMAGE_ZOOM}
                  aria-label="원본 이미지 축소"
                >
                  −
                </button>
                <span>{Math.round(studentImage.zoom * 100)}%</span>
                <button
                  type="button"
                  onClick={() => changeImageZoom(IMAGE_ZOOM_STEP)}
                  disabled={studentImage.zoom >= MAX_IMAGE_ZOOM}
                  aria-label="원본 이미지 확대"
                >
                  +
                </button>
                <button type="button" className="fit-button" onClick={() => setImageZoom(1)}>화면 맞춤</button>
              </div>
            )}
            <div className="student-answer-image-modal__content">
              {studentImage.status === 'loading' && <p>원본 이미지를 불러오는 중입니다.</p>}
              {studentImage.status === 'error' && <p className="image-error">{studentImage.error}</p>}
              {studentImage.url && (
                <div
                  className="student-answer-image-modal__image-stage"
                  style={{ zoom: studentImage.zoom }}
                >
                  <img src={studentImage.url} alt={`${studentImage.studentName} 학생 원본 답안지`} />
                </div>
              )}
            </div>
            <footer>
              <button type="button" onClick={() => setStudentImage(null)}>닫기</button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
};

export default OmrSubmissionResultsModal;
